import type { FC, MouseEvent } from 'react';
import { useEffect } from 'react';
import { twcls } from '@utils/twcls';
import { lockBodyScroll, unlockBodyScroll } from '@utils/scrollLock';
import type { ClassValue } from 'clsx';
import { observer } from 'mobx-react';

interface BackdropModalProps {
  /** Called when the backdrop is dismissed (click or Escape). */
  onClose?: (event?: MouseEvent<HTMLDivElement>) => void;
  className?: ClassValue;
}

/**
 * Full-screen dimming backdrop. Unlike `<Modal>` (a reactjs-popup portal with its own
 * centered content box), this is a plain in-tree overlay that dims the page and closes
 * on click. Used where a feature renders its own floating panel (e.g. the header search
 * dropdown) and only needs the backdrop layer behind it — NOT a portal that would stack
 * above that panel.
 */
const BackdropModal: FC<BackdropModalProps> = observer(
  ({ onClose, className }) => {
    useEffect(() => {
      lockBodyScroll();
      return unlockBodyScroll;
    }, []);

    // Keyboard dismiss: the backdrop itself is decorative (aria-hidden), so give
    // keyboard users an Escape path to close the panel it sits behind.
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
          onClose?.();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onClose]);

    return (
      <div
        aria-hidden='true'
        onClick={onClose}
        className={twcls(
          'fixed inset-0 z-[var(--z-backdrop)] sm:z-[var(--z-backdrop-mobile)] bg-black/50 backdrop-blur-[4px] animate-opacity-slow',
          className
        )}
      />
    );
  }
);

export default BackdropModal;
